import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { ChangeCircle, LogoutRounded } from "@mui/icons-material";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import { useGetProfileCardDataQuery } from "../../features/profileSlice"
import { Puff } from 'react-loader-spinner'
import { colors } from "../../Shared/colors";
import { decryptData } from "../../services/crypto";
import CallIcon from "@mui/icons-material/Call";
import CallMissedIcon from "@mui/icons-material/CallMissed";

const ProfileCard = ({
  user,
  handleLogout,
  handleReset,
}) => {
  const [drop, setDrop] = useState(false);
  const [data, setData] = useState({})
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const dropRef = useRef(null);

  let staffId = null;
  let employeeId = localStorage.getItem("id");

  if (employeeId) {
    try {
      staffId = decryptData(employeeId);
    } catch (error) {
      console.error("Failed to decrypt data:", error);
    }
  }
  if (!staffId) {
    console.error("Staff ID not found in localStorage");
  }

  const { data: res, isLoading, error } = useGetProfileCardDataQuery({ employeeId: staffId })

  useEffect(() => {
    console.log("Profile data query result:", { res, isLoading, error, staffId });
    if (res) {
      console.log("Setting profile data:", res);
      setData(res)
    }
  }, [res, isLoading, error, staffId])
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropRef.current && !dropRef.current.contains(event.target)) {
        setDrop(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const handleLogoutClick = async () => {
    setIsLoggingOut(true);
    try {
      await handleLogout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };
  return (
    <ProfileContainer>
      <Profile onClick={() => setDrop(!drop)}>
        <Img>
          <img src={user?.profilePic} alt="Profile" />
        </Img>
        <KeyboardArrowDownOutlinedIcon />
      </Profile>
      <Drop ref={dropRef} show={drop}>
        <Section>
          <ProfileHeading>Your Profile</ProfileHeading>
          <ProfilePhoto>
            <ProfileImage>
              <img src={user?.profilePic} alt="Profile" />
            </ProfileImage>
            <ProfileDetails>
              <UserName>{user?.firstName} {user?.lastName}</UserName>
              <UserEmail>{user?.email}</UserEmail>
              <UserRole>{user?.designation}</UserRole>
            </ProfileDetails>
          </ProfilePhoto>
        </Section>

        <Section>
          <ProfileHeading>Your Performance</ProfileHeading>
          {isLoading ? (
            <LoadingContainer>
              <Puff height="40" width="40" color={colors.brown} radius="10" visible={true} />
              <LoadingText>Loading performance data...</LoadingText>
            </LoadingContainer>
          ) : error ? (
            <ErrorContainer>
              <ErrorIcon>⚠️</ErrorIcon>
              <ErrorText>Unable to load performance data</ErrorText>
            </ErrorContainer>
          ) : (
            <PerformanceGrid>
              {user?.role === "ts" ? (
                <>
                  <PerformanceItem>
                    <PerformanceIcon>
                      <CallIcon style={{ color: "green" }} />
                    </PerformanceIcon>
                    <p>Connected Call</p>
                    <PerformanceValue>{data?.dpCounts || 0}</PerformanceValue>
                  </PerformanceItem>

                  <PerformanceItem>
                    <PerformanceIcon>
                      <CallMissedIcon style={{ color: "red" }} />
                    </PerformanceIcon>
                    <p>Not Connected</p>
                    <PerformanceValue>{data?.pdpCounts || 0}</PerformanceValue>
                  </PerformanceItem>
                </>
              ) : (
                <>
                  <PerformanceItem>
                    <PerformanceIcon>💰</PerformanceIcon>
                    <p>DP Payment</p>
                    <PerformanceValue>{data?.dpCounts || 0}</PerformanceValue>
                  </PerformanceItem>

                  <PerformanceItem>
                    <PerformanceIcon>💳</PerformanceIcon>
                    <p>PDP Payment</p>
                    <PerformanceValue>{data?.pdpCounts || 0}</PerformanceValue>
                  </PerformanceItem>
                </>
              )}
              <PerformanceItem>
                <PerformanceIcon>📅</PerformanceIcon>
                <p>Meetings</p>
                <PerformanceValue>{data?.meetingCount || 0}</PerformanceValue>
              </PerformanceItem>
            </PerformanceGrid>
          )}
        </Section>
        <ActionButtons>
          <ActionButton onClick={handleLogoutClick} disabled={isLoggingOut}>
            {isLoggingOut ? (
              <Puff height="30" width="30" color={colors.brown} radius="10" visible={true} />
            ) : (
              <>
                <LogoutRounded sx={{ color: "red" }} /> <span>LogOut</span>
              </>
            )}
          </ActionButton>
          <ActionButton onClick={handleReset}>
            <ChangeCircle sx={{ color: "#249393" }} /> <span>Change Password</span>
          </ActionButton>
        </ActionButtons>
      </Drop>
    </ProfileContainer>
  );
};

export default ProfileCard;

const ProfileContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Profile = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 12px;
  transition: all 0.3s ease;
  background: transparent;
  
  &:hover {
    background: linear-gradient(135deg, ${colors.lightWhite} 0%, rgba(36, 147, 147, 0.05) 100%);
    transform: scale(1.02);
  }
  
  @media (max-width: 1200px) and (min-width: 1025px) {
    padding: 0.4rem;
  }
  
  @media (max-width: 1024px) {
    padding: 0.3rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.2rem;
  }
`;

const Img = styled.div`
  position: relative;
  margin-right: 0.5rem;
  
  img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid ${colors.white};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 2px;
    right: 2px;
    width: 12px;
    height: 12px;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border: 2px solid ${colors.white};
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 1200px) and (min-width: 1025px) {
    img {
      width: 36px;
      height: 36px;
    }
    
    &::after {
      width: 10px;
      height: 10px;
    }
  }
  
  @media (max-width: 1024px) {
    img {
      width: 32px;
      height: 32px;
    }
    
    &::after {
      width: 8px;
      height: 8px;
    }
  }
  
  @media (max-width: 768px) {
    img {
      width: 28px;
      height: 28px;
    }
    
    &::after {
      width: 6px;
      height: 6px;
    }
  }
`;

const Drop = styled.div`
  display: ${({ show }) => (show ? "block" : "none")};
  position: absolute;
  top: 100%;
  right: 0;
  background: ${colors.white};
  border-radius: 10px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  width: 280px;
  max-height: 320px;
  overflow: hidden;
  z-index: 1000;
  animation: slideDown 0.3s ease-out;
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media (max-width: 1200px) and (min-width: 1025px) {
    width: 260px;
    max-height: 300px;
  }
  
  @media (max-width: 1024px) {
    width: 240px;
    max-height: 280px;
  }
  
  @media (max-width: 768px) {
    width: 220px;
    max-height: 260px;
    right: -20px;
  }
`;

const Section = styled.div`
  padding: 0.6rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  
  &:last-of-type {
    border-bottom: none;
  }
  
  @media (max-width: 1024px) {
    padding: 0.5rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem;
  }
`;

const ProfileHeading = styled.h4`
  font-size: 0.75rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: ${colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  
  &::before {
    content: '';
    width: 3px;
    height: 12px;
    background: linear-gradient(135deg, ${colors.brown} 0%, #1a6b6b 100%);
    border-radius: 2px;
  }
  
  @media (max-width: 1024px) {
    font-size: 0.7rem;
    margin-bottom: 0.4rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.65rem;
    margin-bottom: 0.3rem;
  }
`;

const ProfilePhoto = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ProfileImage = styled.div`
  position: relative;
  
  img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid ${colors.white};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 1px;
    right: 1px;
    width: 8px;
    height: 8px;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border: 2px solid ${colors.white};
    border-radius: 50%;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 1024px) {
    img {
      width: 35px;
      height: 35px;
    }
    
    &::after {
      width: 6px;
      height: 6px;
    }
  }
  
  @media (max-width: 768px) {
    img {
      width: 30px;
      height: 30px;
    }
    
    &::after {
      width: 5px;
      height: 5px;
    }
  }
`;

const ProfileDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const UserName = styled.p`
  font-weight: 600;
  font-size: 1.1rem;
  margin: 0;
  color: ${colors.text};
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const UserEmail = styled.p`
  font-size: 0.85rem;
  color: ${colors.textLight};
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const UserRole = styled.p`
  font-size: 0.8rem;
  color: ${colors.brown};
  font-weight: 600;
  margin: 0;
  background: linear-gradient(135deg, rgba(36, 147, 147, 0.1) 0%, rgba(36, 147, 147, 0.05) 100%);
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  display: inline-block;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;


const PerformanceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.4rem;
  
  @media (max-width: 768px) {
    gap: 0.3rem;
  }
`;

const PerformanceItem = styled.div`
  text-align: center;
  padding: 0.4rem 0.25rem;
  background: linear-gradient(135deg, ${colors.lightWhite} 0%, rgba(36, 147, 147, 0.02) 100%);
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }

  p {
    margin: 0;
    font-size: 0.6rem;
    color: ${colors.textLight};
    font-weight: 500;
  }
  
  @media (max-width: 1024px) {
    padding: 0.35rem 0.2rem;
    gap: 0.2rem;
    
    p {
      font-size: 0.55rem;
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.3rem 0.15rem;
    gap: 0.15rem;
    
    p {
      font-size: 0.5rem;
    }
  }
`;

const PerformanceIcon = styled.div`
  font-size: 1rem;
  margin-bottom: 0.15rem;
  
  @media (max-width: 1024px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
`;

const LoadingText = styled.p`
  font-size: 0.9rem;
  color: ${colors.textLight};
  margin: 0;
  font-weight: 500;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(239, 68, 68, 0.02) 100%);
  border-radius: 12px;
  border: 1px solid rgba(239, 68, 68, 0.1);
`;

const ErrorIcon = styled.div`
  font-size: 2rem;
`;

const ErrorText = styled.p`
  font-size: 0.9rem;
  color: #ef4444;
  margin: 0;
  font-weight: 500;
  text-align: center;
`;

const PerformanceValue = styled.span`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${colors.brown};
  display: block;
  
  @media (max-width: 1024px) {
    font-size: 1.2rem;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ActionButtons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.4rem;
  padding: 0.6rem;
  background: linear-gradient(135deg, ${colors.lightWhite} 0%, rgba(36, 147, 147, 0.02) 100%);
  
  @media (max-width: 1024px) {
    padding: 0.5rem;
    gap: 0.3rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem;
    gap: 0.25rem;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.4rem 0.3rem;
  border: none;
  border-radius: 5px;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.disabled ? 'rgba(0, 0, 0, 0.05)' : 'transparent'};
  color: ${props => props.disabled ? colors.textLight : colors.text};
  border: 1px solid rgba(0, 0, 0, 0.1);
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, ${colors.brown} 0%, #1a6b6b 100%);
    color: ${colors.white};
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(36, 147, 147, 0.3);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  svg {
    font-size: 0.75rem;
  }
  
  @media (max-width: 1024px) {
    padding: 0.35rem 0.25rem;
    font-size: 0.65rem;
    gap: 0.2rem;
    
    svg {
      font-size: 0.7rem;
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.3rem 0.2rem;
    font-size: 0.6rem;
    gap: 0.15rem;
    
    svg {
      font-size: 0.65rem;
    }
  }
`;
